import React, { useEffect, useState } from "react";
import "./reportProfile.css";
import { ChevronRight } from "lucide-react";
import InnerHeader from "../../components/InnerHeader";

export default function ReportProfile() {
  const [userData, setUserData] = useState({
    username: "",
    vip_coin: 0,
    name: "",
    phone: "",
    session_id: "",
    email: "",
    created_at: "",
    dofb: "",
    email_verified_at: null,
  });

  const [isEditingBirthday, setIsEditingBirthday] = useState(false);
  const [dofb, setDofb] = useState("");
  const [profileLoaded, setProfileLoaded] = useState(false);

  const [isEditingName, setIsEditingName] = useState(false);
  const [nameInput, setNameInput] = useState("");

  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [emailInput, setEmailInput] = useState("");

  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [modalType, setModalType] = useState("error");

  const showMsg = (message, type = "error") => {
    setModalMessage(message || "Something went wrong");
    setModalType(type);
    setShowModal(true);
  };

  const copyText = async (text) => {
    try {
      await navigator.clipboard.writeText(text || "");
      showMsg("Copied successfully", "success");
    } catch (error) {
      console.error("Copy failed:", error);
      showMsg("Copy failed");
    }
  };

  const fetchUserData = async () => {
    const token = localStorage.getItem("token");
    const remoteId = localStorage.getItem("remote_id");

    if (token && remoteId) {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/user/show?remote_id=${remoteId}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.ok) {
          const data = await response.json();
          const formattedDate = data.created_at
            ? new Date(data.created_at).toLocaleDateString("en-GB")
            : "N/A";

          setUserData({
            username: data.username || "Unknown User",
            vip_coin: data.vip_coin || 0,
            name: data.name || "",
            phone: data.phone || "",
            email: data.email || "",
            session_id: data.session_id || "",
            created_at: formattedDate,
            dofb: data.dofb || "",
            email_verified_at: data.email_verified_at || null,
          });

          setDofb(data.dofb || "");

          if (!data.dofb) {
            setIsEditingBirthday(true);
          }

          if (!data.name) {
            setIsEditingName(true);
          }

          if (!data.email) {
            setIsEditingEmail(true);
          }

          setProfileLoaded(true);
        } else {
          console.error("Failed to fetch user data");
          showMsg("Failed to fetch user data");
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
        showMsg("Error fetching user data");
      }
    }
  };

  const updateProfileField = async (payload) => {
    const token = localStorage.getItem("token");
    const remoteId = localStorage.getItem("remote_id");

    if (!token || !remoteId) {
      return { ok: false, message: "Not authenticated" };
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/user/profile/update`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ remote_id: remoteId, ...payload }),
        }
      );

      const data = await response.json();
      return { ok: response.ok, data, message: data?.message };
    } catch (e) {
      return { ok: false, message: "Network error" };
    }
  };

  const saveNameOnce = async () => {
    if (!nameInput || nameInput.trim().length < 2) {
      showMsg("Please enter a valid full name.");
      return;
    }

    const res = await updateProfileField({ name: nameInput.trim() });

    if (res.ok) {
      await fetchUserData();
      setIsEditingName(false);
      showMsg("Full name updated successfully.", "success");
    } else {
      showMsg(res.message || "Failed to update name");
    }
  };

  const saveEmailOnce = async () => {
    const email = emailInput.trim();
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailPattern.test(email)) {
      showMsg("Please enter a valid email address.");
      return;
    }

    const res = await updateProfileField({ email });

    if (res.ok) {
      await fetchUserData();
      setIsEditingEmail(false);
      showMsg("Email updated successfully.", "success");
    } else {
      showMsg(res.message || "Failed to update email");
    }
  };

  const updateDofb = async () => {
    const token = localStorage.getItem("token");
    const remoteId = localStorage.getItem("remote_id");

    if (token && remoteId && dofb) {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/user/profile/update`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              remote_id: remoteId,
              dofb: dofb,
            }),
          }
        );

        if (response.ok) {
          const data = await response.json();

          setUserData((prev) => ({
            ...prev,
            dofb: data.dofb || dofb,
          }));

          setIsEditingBirthday(false);
          showMsg("Date of birth updated successfully.", "success");
        } else {
          showMsg("Failed to update date of birth");
        }
      } catch (error) {
        console.error("Error updating DOFB:", error);
        showMsg("Error updating date of birth");
      }
    }
  };

  useEffect(() => {
    fetchUserData();
  }, []);

  return (
    <div className="profile-app">
      <InnerHeader title="Personal Information" />

      <div className="profile-list">
        <div className="row">
          <span className="label">Username</span>
          <span className="right">
            <span className="value edit-rep">{userData.username}</span>

            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#8d9aa5"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ cursor: "pointer" }}
              onClick={() => copyText(userData.username)}
            >
              <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
            </svg>
          </span>
        </div>

        <div className="row">
          <span className="label">Full legal name</span>

          <span className="right column">
            {profileLoaded && (!userData.name || isEditingName) ? (
              <>
                <input
                  type="text"
                  className="personal-number"
                  value={nameInput}
                  onChange={(e) => setNameInput(e.target.value)}
                  placeholder="Full Name"
                />

                <div className="button" onClick={saveNameOnce}>
                  Save
                </div>
              </>
            ) : (
              <>
                <div
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  <span className="value edit-rep">
                    {userData.name || "N/A"}
                  </span>

                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#8d9aa5"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    style={{ cursor: "pointer" }}
                    onClick={() => copyText(userData.name || "")}
                  >
                    <rect
                      x="9"
                      y="9"
                      width="13"
                      height="13"
                      rx="2"
                      ry="2"
                    ></rect>
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                  </svg>
                </div>

                {(!userData.name || userData.name === "N/A") && (
                  <span
                    className="edit-icon"
                    onClick={() => setIsEditingName(true)}
                    style={{ cursor: "pointer", marginTop: "8px" }}
                  >
                    ✏️
                  </span>
                )}
              </>
            )}
          </span>
        </div>

        <div className="row">
          <span className="label">Date of birth</span>

          <input
            type="date"
            placeholder="YYYY/MM/DD"
            disabled={!isEditingBirthday}
            className="inn"
            value={dofb}
            onChange={(e) => setDofb(e.target.value)}
          />

          <div
            onClick={() => {
              if (!isEditingBirthday) return;
              updateDofb();
            }}
            className={`status save-btn ${!isEditingBirthday ? "disabled" : ""}`}
            style={{
              display: "block",
              pointerEvents: isEditingBirthday ? "auto" : "none",
              opacity: isEditingBirthday ? 1 : 0.5,
              cursor: isEditingBirthday ? "pointer" : "not-allowed",
            }}
          >
            Save
          </div>
        </div>

        <div className="row">
          <span className="label">Phone</span>
          <span className="right">
            <span className="value verified">
              {userData.phone || "+880 1998604051"}
            </span>

            <div className="verified-check">
              <img width={27} src="/images/Margin.svg" alt="verified" />
            </div>

            <ChevronRight size={16} color="#8d9aa5" />
          </span>
        </div>

        <div className="row">
          <span className="label">Email</span>

          <span
            className="right"
            style={{ display: "flex", alignItems: "center", gap: "8px" }}
          >
            {profileLoaded && (!userData.email || isEditingEmail) ? (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px",
                  width: "100%",
                }}
              >
                <input
                  type="email"
                  className="personal-number"
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                  placeholder="Enter your email"
                />

                <div className="member-content">
                  <div className="button" onClick={saveEmailOnce}>
                    Save
                  </div>
                </div>
              </div>
            ) : (
              <>
                <span
                  className={`value ${
                    userData.email_verified_at ? "verified" : ""
                  }`}
                >
                  {userData.email || "N/A"}
                </span>

                {userData.email_verified_at ? (
                  <div className="verified-check">
                    <img width={27} src="/images/Margin.svg" alt="verified" />
                  </div>
                ) : (
                  <span className="status unconfirm-btn">Not Verified</span>
                )}
              </>
            )}
          </span>
        </div>
      </div>

      {showModal && (
        <div
          className="profile-modal-overlay"
          onClick={() => setShowModal(false)}
        >
          <div
            className={`profile-modal-box ${modalType}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="profile-modal-header">
              <h3>{modalType === "success" ? "Success" : "Notice"}</h3>
              <button
                type="button"
                className="profile-modal-close"
                onClick={() => setShowModal(false)}
              >
                ×
              </button>
            </div>

            <div className="profile-modal-body">
              <p>{modalMessage}</p>
            </div>

            <div className="profile-modal-footer">
              <button
                type="button"
                className="profile-modal-ok-btn"
                onClick={() => setShowModal(false)}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}